import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { PartnerDocumentItem, PartnerLiquidationDetail, PartnerLiquidationRow, PartnerLiquidationRowDiscount } from '../../../../shared/models/partner-liquidation-detail';
import { Bus } from '../../../../shared/models/bus';
import { AdminService } from '../../services/admin';
import { AlertService } from '../../../../core/ui/alert';
import { convertFirestoreTimestampToDate } from '../../../../shared/utils/date-converter';

type InvoiceRowItem = {
  row: PartnerLiquidationRow;
  totalToInvoice: number;
  netAmount: number;
  invoice: PartnerDocumentItem;
  deposit: PartnerDocumentItem;
};

@Component({
  selector: 'app-liquidation-review-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  templateUrl: './liquidation-review-detail.html',
  styleUrl: './liquidation-review-detail.css'
})
export class LiquidationReviewDetail implements OnInit {
  private readonly emptyRowGroup: Array<{ entityId: string; entityName: string; rows: InvoiceRowItem[] }> = [];
  liquidationId: string = '';
  partnerId: string = '';
  partnerName: string = '';
  detail: PartnerLiquidationDetail | null = null;
  invoiceRows: InvoiceRowItem[] = [];
  invoiceRowsByEntity: Array<{ entityId: string; entityName: string; rows: InvoiceRowItem[] }> = [];
  invoiceRowsToBillByEntity: Array<{ entityId: string; entityName: string; rows: InvoiceRowItem[] }> = [];
  depositRowsToBillByEntity: Array<{ entityId: string; entityName: string; rows: InvoiceRowItem[] }> = [];
  rejectionReason: string = '';
  loading = false;
  busMap: Map<string, Bus> = new Map();

  statusTranslations: Record<string, string> = {
    'DRAFT'                        : 'Borrador',
    'UPLOADING'                    : 'Subiendo Archivo',
    'PROCESSING_EXCEL'             : 'Procesando Excel',
    'PROCESSED_EXCEL'              : 'Excel procesado',
    'COMPLETED'                    : 'Completada',
    'NOTIFICATIONS_SENT'           : 'Notificaciones enviadas',
    'FAILED_INVALID_UPLOAD_PATH'   : 'Falló: Ruta de Carga Inválida',
    'FAILED_EMPTY_FILE'            : 'Falló: Archivo Vacío',
    'FAILED_INITIAL_STATUS_UPDATE' : 'Falló: Actualización Inicial de Estado',
    'FAILED_INVALID_FILE_TYPE'     : 'Falló: Tipo de Archivo Inválido',
    'FAILED_NO_DATA'               : 'Falló: Sin Datos en Excel',
    'FAILED_FILE_CORRUPT'          : 'Falló: Archivo Corrupto',
    'FAILED_EXCEL_EMPTY_OR_NO_DATA': 'Falló: Excel Vacío o Sin Datos',
    'FAILED_NO_VALID_BUSES'        : 'Falló: Sin Buses Válidos',
    'FAILED_MASTER_DATA_FETCH'     : 'Falló: Error en Carga de Datos Maestros',
    'FAILED_EXCEL_FORMAT'          : 'Falló: Formato de Excel Inválido',
    'FAILED_PROCESSING_ERROR'      : 'Falló: Error de Procesamiento',
    'FAILED_TIMEOUT'               : 'Falló: Tiempo de Espera Excedido',
    'PENDING_UPLOAD'               : 'Pendiente de envío',
    'NOTIFICATION_FAILED'          : 'Notificación fallida',
    'REVIEW_IN_PROGRESS'           : 'En revisión',
    'REJECTED'                     : 'Rechazada',
    'APPROVED'                     : 'Aprobada'
  };

  get totalToInvoice(): number {
    return this.invoiceRows.reduce((sum, row) => sum + (Number(row.totalToInvoice) || 0), 0);
  }

  get invoiceRowsToBill() {
    return this.invoiceRows.filter(row => (Number(row.totalToInvoice) || 0) > 0);
  }

  get depositRowsToBill() {
    return this.invoiceRows.filter(row => (Number(row.netAmount) || 0) < 0);
  }

  get totalToDeposit(): number {
    const net = this.netAmount;
    return net < 0 ? Math.abs(net) : 0;
  }

  get totalDiscounts(): number {
    return this.invoiceRows.reduce((sum, row) => sum + this.calculateRowDiscountTotal(row.row), 0);
  }

  get netAmount(): number {
    return this.roundToTwo(this.totalToInvoice - this.totalDiscounts);
  }

  get canReview(): boolean {
    return this.detail?.status === 'REVIEW_IN_PROGRESS';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.liquidationId = this.route.snapshot.paramMap.get('id') || '';
    this.partnerId = this.route.snapshot.paramMap.get('partnerId') || '';

    if(this.liquidationId && this.partnerId) {
      this.loadDetail();
    }
  }

  async loadDetail(): Promise<void> {
    await this.loadBuses();
    const result = await this.adminService.getPartnerLiquidationDetailForReview(this.liquidationId, this.partnerId);
    if(result) {
      this.partnerName = result.partnerName;
      this.detail = result.detail;
      this.invoiceRows = this.buildInvoiceRows(this.detail);
      this.buildEntityGroups();
      if(this.detail.status === 'REJECTED' && this.detail.rejectionReason) {
        this.rejectionReason = this.detail.rejectionReason;
      } else {
        this.rejectionReason = '';
      }
    }
  }

  private async loadBuses(): Promise<void> {
    if(this.busMap.size > 0) {
      return;
    }
    const buses = await this.adminService.getAllBuses();
    if(buses && buses.length > 0) {
      this.busMap = new Map(buses.map((b: Bus) => [b.id, b]));
    }
  }

  get isDaily(): boolean {
    return this.detail?.billingMode === 'DAILY';
  }

  get isWeekly(): boolean {
    return this.detail?.billingMode === 'WEEKLY';
  }

  getBusLabel(busId: string): string {
    return this.busMap.get(busId)?.name || busId;
  }

  getRowGroupLabel(row: PartnerLiquidationRow): string {
    if(row.group && row.group.trim() !== '') {
      return row.group;
    }
    if(row.busName && row.busName.trim() !== '') {
      return row.busName;
    }
    return this.getBusLabel(row.busId);
  }

  private buildEntityGroups(): void {
    this.invoiceRowsByEntity = this.groupRowsByEntity(this.invoiceRows);
    this.invoiceRowsToBillByEntity = this.groupRowsByEntity(this.invoiceRowsToBill);
    this.depositRowsToBillByEntity = this.groupRowsByEntity(this.depositRowsToBill);
  }

  private groupRowsByEntity(rows: InvoiceRowItem[]): Array<{ entityId: string; entityName: string; rows: InvoiceRowItem[] }> {
    if(!rows.length) {
      return this.emptyRowGroup;
    }
    const groupsMap = new Map<string, { entityId: string; entityName: string; rows: InvoiceRowItem[] }>();

    rows.forEach(item => {
      let entityId = item.row.entityId || '';
      let entityName = item.row.entityName || '';

      if(!entityId || !entityName) {
        const bus = this.busMap.get(item.row.busId);
        entityId = entityId || bus?.entityId || '';
        entityName = entityName || bus?.entityName || '';
      }

      if(!entityId) {
        entityId = 'unknown';
      }

      if(!entityName) {
        entityName = 'Entidad desconocida';
      }

      if(!groupsMap.has(entityId)) {
        groupsMap.set(entityId, { entityId, entityName, rows: [] });
      }

      groupsMap.get(entityId)!.rows.push(item);
    });

    return Array.from(groupsMap.values())
      .filter(group => group.rows.length > 0)
      .sort((a, b) => a.entityName.localeCompare(b.entityName));
  }

  formatDateDisplay(value: any): string {
    const date = convertFirestoreTimestampToDate(value);
    if(!date || isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private calculateRowDiscountTotal(row: { discounts?: PartnerLiquidationRowDiscount[]; isrAmount?: number; retentionIVAAmount?: number }): number {
    const discountsTotal = (row.discounts || []).reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    const isrAmount = Number(row.isrAmount) || 0;
    const retentionAmount = Number(row.retentionIVAAmount) || 0;
    return this.roundToTwo(discountsTotal + isrAmount + retentionAmount);
  }

  private calculateRowTotal(row: PartnerLiquidationRow): number {
    const passages = Number(row.passagesAmount) || 0;
    return this.roundToTwo(passages);
  }

  private calculateRowNet(row: PartnerLiquidationRow): number {
    const passages = Number(row.passagesAmount) || 0;
    const discounts = this.calculateRowDiscountTotal(row);
    return this.roundToTwo(passages - discounts);
  }

  private roundToTwo(value: number): number {
    return Math.round((Number(value) || 0) * 100) / 100;
  }

  private buildInvoiceRows(detail: PartnerLiquidationDetail): InvoiceRowItem[] {
    const rows = Array.isArray(detail.rows) ? detail.rows : [];
    const fallbackInvoices = Array.isArray(detail.invoices) ? detail.invoices : [];
    const fallbackDeposits = Array.isArray(detail.deposits) ? detail.deposits : [];

    return rows.map((row, index) => {
      const hasRowInvoice = row.invoice && (row.invoice.url || row.invoice.fileName || row.invoice.number || row.invoice.date);
      const baseInvoice = hasRowInvoice
        ? row.invoice
        : (fallbackInvoices[index] || { url: '', fileName: '', number: '', date: '', amount: 0 });

      const totalToInvoice = this.calculateRowTotal(row);
      const netAmount = this.calculateRowNet(row);
      const invoice: PartnerDocumentItem = {
        url      : baseInvoice.url || '',
        fileName : baseInvoice.fileName || '',
        number   : baseInvoice.number || '',
        date     : baseInvoice.date || '',
        amount   : totalToInvoice
      };

      const hasRowDeposit = row.deposit && (row.deposit.url || row.deposit.fileName || row.deposit.number || row.deposit.date);
      const baseDeposit = hasRowDeposit ? row.deposit : fallbackDeposits[index];

      const deposit: PartnerDocumentItem = {
        url      : baseDeposit?.url || '',
        fileName : baseDeposit?.fileName || '',
        number   : baseDeposit?.number || '',
        date     : baseDeposit?.date || '',
        amount   : this.roundToTwo(Math.abs(netAmount))
      };

      return { row, totalToInvoice, netAmount, invoice, deposit };
    });
  }

  getTranslatedStatus(status: string): string {
    return this.statusTranslations[status] || status;
  }

  async onApprove(): Promise<void> {
    if(!this.detail) return;
    this.loading = true;
    const ok = await this.adminService.reviewPartnerLiquidation(this.liquidationId, this.partnerId, 'APPROVED');
    this.loading = false;
    if(ok) {
        await this.loadDetail();
    }
  }

  async onReject(): Promise<void> {
    if(!this.detail) return;
    if(!this.rejectionReason || this.rejectionReason.trim() === '') {
        this.alertService.error('El motivo de rechazo es requerido.', 'Error');
        return;
    }
    this.loading = true;
    const ok = await this.adminService.reviewPartnerLiquidation(this.liquidationId, this.partnerId, 'REJECTED', this.rejectionReason.trim());
    this.loading = false;
    if(ok) {
        await this.loadDetail();
    }
  }

  goBack(): void {
    if(this.liquidationId) {
      this.router.navigate(['/liquidations', this.liquidationId, 'reviews']);
    } else {
      this.router.navigate(['/liquidations']);
    }
  }
}
